import {
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ApplicationButton, LocalizedTextField, UploadAvatarDialog as UploadAvatarDialogJS } from '../../../../MicrospecDynamicUI/Components';
import { useOrganizationContext } from '../../../../MicrospecDynamicUI/Dashboard/OrganizationContext';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById, saveDocument } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessCategory';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { BRANCH_ID, ORG_ID } from '../../../constants/collectionPaths';

const UploadAvatarDialog = UploadAvatarDialogJS as any;

interface UpsertCategoryProps {
  categoryId?: string;
  onSave?: (category: BusinessCategory) => void;
  onCancel?: () => void;
}

const UpsertCategory: React.FC<UpsertCategoryProps> = ({
  categoryId,
  onSave,
  onCancel,
}) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const theme = useTheme();
  const { usedLocalization } = useOrganizationContext();
  const organizationId = ORG_ID;
  const branchId = BRANCH_ID;
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Stable ID for storage path (persists for new categories within this session)
  const tempIdRef = useRef<string>(`temp_${Date.now()}`);
  const storageId = categoryId || tempIdRef.current;
  const storageRef = `organizations/${organizationId}/branches/${branchId}/categories/${storageId}/image.jpg`;

  const [formData, setFormData] = useState<BusinessCategory>({
    name: usedLocalization?.supported?.length > 1 ? {} : '',
    description: usedLocalization?.supported?.length > 1 ? {} : '',
    image: '',
    is_published: true,
    order: 0,
  });

  // Load existing category if editing
  useEffect(() => {
    const loadCategory = async () => {
      if (!categoryId || !db || !organizationId || !branchId) return;

      setLoading(true);
      try {
        const collectionPath = `organizations/${organizationId}/branches/${branchId}/categories`;
        const categoryData = await getDocById(
          null,
          null,
          db,
          collectionPath,
          categoryId,
          false
        );

        if (categoryData) {
          setFormData({
            name: categoryData.name || '',
            description: categoryData.description || '',
            image: categoryData.image || '',
            is_published: categoryData.is_published ?? true,
            order: categoryData.order || 0,
            created_at: categoryData.created_at,
            updated_at: categoryData.updated_at,
          });
        }
      } catch (err) {
        console.error('Error loading category:', err);
        setError(translate('category.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadCategory();
  }, [categoryId, db, organizationId, branchId, translate]);

  const handleInputChange = (field: keyof BusinessCategory, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (formData.name && typeof formData.name === 'string' && !formData.name.trim()) {
      setError(translate('category.nameRequired'));
      return false;
    }
    if (formData.name && typeof formData.name === 'object') {
      const hasValidLocale = Object.values(formData.name).some(name => name.trim() !== '');
      if (!hasValidLocale) {
        setError(translate('category.nameRequired'));
        return false;
      }
    }
    if (formData.order < 0) {
      setError(translate('category.orderMustBePositive'));
      return false;
    }
    if (!formData.image) {
      setError(translate('category.imageRequired'));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (!db || !organizationId || !branchId) {
      setError(translate('category.missingContext'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const collectionPath = `organizations/${organizationId}/branches/${branchId}/categories`;

      const categoryData: BusinessCategory = {
        ...formData,
        created_at: categoryId ? formData.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const result = await saveDocument(
        firestore,
        collectionPath,
        categoryData,
        { id: categoryId }
      );

      setSuccess(true);

      if (onSave) {
        onSave({ ...categoryData, id: result.id });
      } else {
        // Reset form only if no onSave callback (standalone mode)
        if (!categoryId) {
          setFormData({
            name: '',
            description: '',
            image: '',
            is_published: true,
            order: 0,
          });
        }
        setSuccess(false);
      }

    } catch (err: any) {
      setError(err.message || translate('category.failedToSave'));
      console.error('Error saving category:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Reset form
      setFormData({
        name: '',
        description: '',
        image: '',
        is_published: true,
        order: 0,
      });
      setError(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark'
            ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`
            : `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 3,
            color: theme.palette.mode === 'dark'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          }}
        >
          {categoryId ? translate('category.editTitle') : translate('category.createTitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {translate('category.savedSuccessfully')}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Category Name */}
          <Grid item xs={12} md={6}>
            {usedLocalization?.supported?.length > 1 ? (
              <LocalizedTextField
                languageConfig={usedLocalization}
                value={typeof formData.name === 'object' ? formData.name : { [usedLocalization.preferred]: formData.name || '' }}
                onChange={(value) => handleInputChange('name', value)}
                label={translate('category.name')}
                required
                helperText={translate('category.enterName')}
                variant="outlined"
              />
            ) : (
              <TextField
                fullWidth
                label={translate('category.name')}
                value={typeof formData.name === 'string' ? formData.name : formData.name?.[usedLocalization?.preferred || 'en-US'] || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                helperText={translate('category.enterName')}
                variant="outlined"
              />
            )}
          </Grid>

          {/* Order */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={translate('category.order')}
              type="number"
              value={formData.order}
              onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
              helperText={translate('category.orderHelperText')}
              variant="outlined"
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            {usedLocalization?.supported?.length > 1 ? (
              <LocalizedTextField
                languageConfig={usedLocalization}
                value={typeof formData.description === 'object' ? formData.description : { [usedLocalization.preferred]: formData.description || '' }}
                onChange={(value) => handleInputChange('description', value)}
                label={translate('category.description')}
                multiline
                rows={3}
                helperText={translate('category.enterDescription')}
                variant="outlined"
              />
            ) : (
              <TextField
                fullWidth
                label={translate('category.description')}
                value={typeof formData.description === 'string' ? formData.description : formData.description?.[usedLocalization?.preferred || 'en-US'] || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                helperText={translate('category.enterDescription')}
                variant="outlined"
              />
            )}
          </Grid>

          {/* Category Image */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {translate('category.image')}
              <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 480,
                aspectRatio: '16 / 9',
                borderRadius: 2,
                overflow: 'hidden',
                border: `2px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover .image-overlay': { opacity: 1 },
              }}
              onClick={() => setUploadDialogOpen(true)}
            >
              {formData.image ? (
                <>
                  <Box
                    component="img"
                    src={formData.image}
                    alt="category"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <Box
                    className="image-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: alpha(theme.palette.common.black, 0.5),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: '#fff',
                      gap: 1,
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 32 }} />
                    <Typography variant="body2">{translate('category.changeImage')}</Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: alpha(theme.palette.primary.main, 0.7) }}>
                  <CloudUploadIcon sx={{ fontSize: 48 }} />
                  <Typography variant="body2" fontWeight={500}>{translate('category.uploadImage')}</Typography>
                  <Typography variant="caption" color="text.secondary">{translate('category.imageHelperText')}</Typography>
                </Box>
              )}
            </Box>
            {formData.image && (
              <Button
                size="small"
                color="error"
                sx={{ mt: 1 }}
                onClick={() => handleInputChange('image', '')}
              >
                {translate('category.removeImage')}
              </Button>
            )}
          </Grid>

          {/* Published Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_published}
                  onChange={(e) => handleInputChange('is_published', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {translate('category.published')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.is_published
                      ? translate('category.visibleToUsers')
                      : translate('category.hiddenFromUsers')}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <ApplicationButton
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving}
                size="large"
              >
                {translate('common.cancel')}
              </ApplicationButton>
              <ApplicationButton
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="large"
                sx={{
                  minWidth: 150,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
                    : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                {saving ? translate('common.loading') : translate('common.save')}
              </ApplicationButton>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <UploadAvatarDialog
      open={uploadDialogOpen}
      onClose={() => setUploadDialogOpen(false)}
      onImageUploaded={(url: string) => {
        handleInputChange('image', url);
        setUploadDialogOpen(false);
      }}
      currentImageUrl={formData.image || null}
      storageRef={storageRef}
      aspectRatio={16 / 9}
      cropShape="rect"
      maxWidth={800}
      quality={0.85}
    />
    </>
  );
};

export default UpsertCategory;
